import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Star, ArrowLeft, Phone } from 'lucide-react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { services, serviceDetails } from '../data/siteData';

const ServiceDetailPage = ({ onBookingClick }) => {
  const { serviceId } = useParams();
  const navigate = useNavigate();

  // Testimonials Carousel
  const [emblaRefTestimonials, emblaApiTestimonials] = useEmblaCarousel(
    { 
      loop: true, 
      align: 'center',
      slidesToScroll: 1,
      skipSnaps: false
    },
    [Autoplay({ delay: 4000, stopOnInteraction: false, stopOnMouseEnter: false })]
  );

  const [selectedIndex, setSelectedIndex] = useState(0);

  const onSelect = useCallback(() => {
    if (!emblaApiTestimonials) return;
    setSelectedIndex(emblaApiTestimonials.selectedScrollSnap());
  }, [emblaApiTestimonials]);

  useEffect(() => {
    if (!emblaApiTestimonials) return;
    onSelect();
    emblaApiTestimonials.on('select', onSelect);
    emblaApiTestimonials.on('reInit', onSelect);
  }, [emblaApiTestimonials, onSelect]);

  // Get service data
  const serviceKey = Object.keys(services).find(key => serviceDetails[key]?.slug === serviceId);
  
  if (!serviceKey) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4">Service Not Found</h1>
          <button onClick={() => navigate('/services')} className="text-amber-500 hover:underline">
            Back to Services
          </button>
        </div>
      </div>
    );
  }

  const service = services[serviceKey];
  const detail = serviceDetails[serviceKey];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section with Background Image */}
      <section 
        className="relative h-[60vh] min-h-[500px] flex items-center justify-center"
        style={{
          backgroundImage: `url(${detail.heroImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/70 to-black/60"></div>
        
        {/* Content */}
        <div className="relative z-10 w-full px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-amber-500 text-sm md:text-base uppercase tracking-widest mb-4 font-bold">
            {detail.subtitle}
          </p>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6 leading-tight">
            {detail.title}
          </h1>
          <button
            onClick={() => navigate('/services')}
            className="inline-flex items-center gap-2 text-white hover:text-amber-500 transition-colors text-lg"
          >
            <ArrowLeft size={20} />
            <span className="font-medium">Back to Services</span>
          </button>
        </div>
      </section>

      {/* About Service Section - Image Left, Content Right */}
      <section className="py-20 bg-white">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Image Left */}
            <div className="order-2 lg:order-1">
              <img
                src={detail.about.image}
                alt={detail.about.title}
                className="w-full h-auto max-h-[450px] object-cover rounded-lg shadow-xl"
              />
            </div>

            {/* Content Right */}
            <div className="order-1 lg:order-2 flex flex-col">
              <p className="text-amber-500 text-sm uppercase tracking-widest mb-4 font-medium">
                {service.subtitle}
              </p>
              <h1 className="text-4xl md:text-5xl font-bold text-black mb-6">
                {detail.about.title}
              </h1>
              <p className="text-gray-600 text-lg leading-relaxed mb-8">
                {detail.about.content}
              </p>
              <button
                onClick={onBookingClick}
                className="bg-amber-500 text-black px-8 py-4 text-lg font-bold uppercase tracking-wider hover:bg-amber-600 hover:text-black transition-all transform hover:scale-105 self-start"
              >
                Book Appointment
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section - Content Left, Image Right */}
      <section className="py-16 bg-gray-50">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
            {/* Content Left */}
            <div className="flex flex-col">
              <h2 className="text-3xl md:text-4xl font-bold text-black mb-4 leading-tight">
                {detail.benefits.title}
              </h2>
              <p className="text-gray-600 text-base leading-relaxed mb-6">
                {detail.benefits.content}
              </p>
              
              {/* Benefits List */}
              <ul className="space-y-3 mb-6">
                {detail.benefits.list.map((benefit, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-5 h-5 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-white text-xs font-bold">✓</span>
                    </div>
                    <span className="text-gray-700 text-base">{benefit}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={onBookingClick}
                className="bg-black text-white px-8 py-3 text-base font-bold uppercase tracking-wider hover:bg-gray-800 transition-all transform hover:scale-105 self-start"
              >
                Get Started
              </button>
            </div>

            {/* Image Right */}
            <div>
              <img
                src={detail.benefits.image}
                alt={detail.benefits.title}
                className="w-full h-auto max-h-[400px] object-cover rounded-lg shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-white">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-6">
              Service Pricing
            </h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto">
              {detail.pricingIntro}
            </p>
          </div>

          {/* Pricing Grid - Center Aligned */}
          <div className="flex flex-wrap justify-center gap-6 max-w-7xl mx-auto">
            {service.services.map((item) => (
              <div
                key={item.id}
                onClick={onBookingClick}
                className="bg-white border-2 border-gray-200 rounded-lg overflow-hidden hover:border-amber-500 hover:shadow-lg transition-all cursor-pointer w-full sm:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)] max-w-sm"
              >
                {item.image && (
                  <div className="w-full h-48 bg-gray-100 flex items-center justify-center">
                    <img 
                      src={item.image} 
                      alt={item.name}
                      className="w-full h-full object-contain p-4"
                    />
                  </div>
                )}
                <div className="p-6 text-center">
                  <h3 className="text-xl font-bold text-black mb-2">{item.name}</h3>
                  <p className="text-gray-600 text-sm mb-4">{item.description}</p>
                  <div className="flex items-baseline gap-2 justify-center">
                    <span className="text-3xl font-bold text-amber-500">{item.price}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section - Carousel Style */}
      <section className="py-20 bg-black">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-amber-500 text-sm uppercase tracking-widest mb-4 font-medium">Client Reviews</p>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              What Our Clients Say
            </h2>
            <p className="text-gray-400 text-lg">
              Real experiences from satisfied customers
            </p>
          </div>

          {/* Testimonials Carousel */}
          <div className="overflow-hidden -mx-4" ref={emblaRefTestimonials}>
            <div className="flex items-center">
              {detail.testimonials.map((testimonial, index) => {
                const isCenter = index === selectedIndex % detail.testimonials.length;
                const bgColor = isCenter ? 'bg-gradient-to-br from-amber-500 to-amber-600' : 'bg-white';
                const textColor = isCenter ? 'text-white' : 'text-gray-800';
                const roleColor = isCenter ? 'text-white/90' : 'text-amber-600';
                const starColor = isCenter ? 'fill-white text-white' : 'fill-amber-500 text-amber-500';
                const quoteColor = isCenter ? 'text-white/20' : 'text-gray-200';
                
                return (
                  <div 
                    key={testimonial.id} 
                    className="min-w-0 flex-[0_0_100%] md:flex-[0_0_50%] lg:flex-[0_0_33.333%] px-4 transition-all duration-500"
                    style={{
                      filter: isCenter ? 'blur(0px)' : 'blur(4px)',
                      opacity: isCenter ? 1 : 0.4,
                      transform: isCenter ? 'scale(1.05)' : 'scale(0.95)'
                    }}
                  >
                    <div className={`${bgColor} p-8 shadow-2xl h-full relative overflow-hidden rounded-lg`}>
                      {/* Decorative Element */}
                      <div className={`absolute top-0 right-0 w-32 h-32 ${isCenter ? 'bg-white/10' : 'bg-amber-500/10'} rounded-full -mr-16 -mt-16`}></div>
                      
                      {/* Name and Role */}
                      <div className="mb-4">
                        <h4 className={`font-bold text-xl mb-1 ${textColor}`}>{testimonial.name}</h4>
                        <p className={`text-sm ${textColor} opacity-80`}>{testimonial.role}</p>
                      </div>

                      {/* Stars */}
                      <div className="flex gap-1 mb-4">
                        {[...Array(5)].map((_, i) => {
                          const fillStar = i < Math.floor(testimonial.rating);
                          const halfStar = i === Math.floor(testimonial.rating) && testimonial.rating % 1 !== 0;
                          
                          return (
                            <div key={i} className="relative">
                              <Star size={18} className={fillStar ? starColor : `text-gray-300`} fill={fillStar ? 'currentColor' : 'none'} />
                              {halfStar && (
                                <div className="absolute top-0 left-0 w-1/2 overflow-hidden">
                                  <Star size={18} className={starColor} fill="currentColor" />
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {/* Quote */}
                      <p className={`${textColor} relative z-10 text-base leading-relaxed`}>
                        "{testimonial.text}"
                      </p>

                      {/* Quote Icon */}
                      <div className={`absolute bottom-4 right-4 ${quoteColor} text-6xl font-serif leading-none`}>"</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section with Background */}
      <section 
        className="relative py-32 overflow-hidden"
        style={{
          backgroundImage: `url(${detail.cta.backgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/50"></div>
        
        <div className="relative z-10 w-full px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            {detail.cta.title}
          </h2>
          <p className="text-xl md:text-2xl text-amber-500 mb-8 font-medium">
            {detail.cta.subtitle}
          </p>
          <p className="text-lg text-gray-200 mb-12 max-w-3xl mx-auto leading-relaxed">
            {detail.cta.content}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
            <button
              onClick={onBookingClick}
              className="bg-amber-500 text-black px-10 py-5 text-lg font-bold uppercase tracking-wider hover:bg-amber-600 hover:text-black transition-all transform hover:scale-105 shadow-lg"
            >
              Book Now
            </button>
            <a
              href="tel:+971564740785"
              className="bg-white text-black px-10 py-5 text-lg font-bold uppercase tracking-wider hover:bg-gray-100 transition-all transform hover:scale-105 flex items-center gap-3 shadow-lg"
            >
              <Phone size={20} />
              Call Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetailPage;
